import { jwtDecode } from 'jwt-decode';
import { env } from '../env';
export type HttpMethod =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'PATCH'
  | 'DELETE'
  | 'OPTIONS';

export type RequestBody =
  | string
  | Blob
  | FormData
  | URLSearchParams
  | null
  | undefined;

interface RequestOptions {
  body?: RequestBody;
  token?: string | null;
  method?: HttpMethod;
  refreshAccessToken?: (() => Promise<string | null>) | null;
}

const TOKEN_EXPIRY_BUFFER_MS = 5_000;
let refreshPromise: Promise<string | null> | null = null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getMessageFromErrorBody = (body: unknown): string | null => {
  if (typeof body === 'string') {
    return body.trim() || null;
  }

  if (!isRecord(body)) {
    return null;
  }

  const { message, error, errors } = body;

  if (typeof message === 'string') {
    return message.trim() || null;
  }

  if (typeof error === 'string') {
    return error.trim() || null;
  }

  if (isRecord(error)) {
    const errorMessage = getMessageFromErrorBody(error);
    if (errorMessage) {
      return errorMessage;
    }
  }

  if (Array.isArray(errors)) {
    for (const item of errors) {
      const itemMessage = getMessageFromErrorBody(item);
      if (itemMessage) {
        return itemMessage;
      }
    }
  }

  return null;
};

/**
 * A failed API request. `message` is the readable message from the response
 * body (unchanged from before, so existing callers behave the same); `status`
 * and the body's discriminating `code` (e.g. PREPAYMENT_REQUIRED) let callers
 * react to specific failures.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  /**
   * A finer, stable reason the API gives with some codes (e.g. with
   * PREPAYMENT_REQUIRED: budget_used_up, renewal_needs_approval,
   * renewal_declined). Branch on this, never on the message text.
   */
  readonly reason?: string;

  constructor(message: string, status: number, code?: string, reason?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.reason = reason;
  }
}

export const isApiError = (error: unknown): error is ApiError =>
  error instanceof ApiError;

const getErrorDetails = async (
  response: Response
): Promise<{ message: string; code?: string; reason?: string }> => {
  const fallback = response.statusText || 'Something went wrong';
  const text = await response.text();

  if (!text) {
    return { message: fallback };
  }

  try {
    const parsed: unknown = JSON.parse(text);
    const code =
      isRecord(parsed) && typeof parsed.code === 'string'
        ? parsed.code
        : undefined;
    // Extra fields of an API error body arrive under `details`.
    const details =
      isRecord(parsed) && isRecord(parsed.details) ? parsed.details : undefined;
    const reasonValue = details?.reason ?? (isRecord(parsed) && parsed.reason);
    const reason = typeof reasonValue === 'string' ? reasonValue : undefined;
    return {
      message: getMessageFromErrorBody(parsed) || fallback,
      code,
      reason,
    };
  } catch {
    return { message: getMessageFromErrorBody(text) || fallback };
  }
};

export const getTenantIdFromCookies = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'tenantId') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

const isTokenExpiringSoon = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    return Date.now() + TOKEN_EXPIRY_BUFFER_MS >= exp * 1000;
  } catch {
    return true;
  }
};

export async function makeApiRequest<T>(
  url: string,
  {
    body,
    token = null,
    method = 'GET',
    refreshAccessToken = null,
  }: RequestOptions = {}
): Promise<T | null> {
  const headers = new Headers();

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
    if (refreshAccessToken && isTokenExpiringSoon(token)) {
      refreshPromise ??= refreshAccessToken();

      const newToken = await refreshPromise;
      refreshPromise = null;

      if (newToken) {
        headers.set('Authorization', `Bearer ${newToken}`);
      }
    }
  }

  // Always forward the tenant context when known — even for unauthenticated
  // requests such as tenant-staff/user login — so the API can resolve the
  // tenant from the header instead of relying solely on the origin subdomain.
  const tenantId = getTenantIdFromCookies();
  if (tenantId) {
    headers.set('x-tenant-id', tenantId);
  }

  if (typeof body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${env.api}${url}`, {
    method,
    body,
    headers,
  });
  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }

    const { message, code, reason } = await getErrorDetails(response);
    throw new ApiError(
      message || response.statusText,
      response.status,
      code,
      reason
    );
  }

  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    return (await response.json()) as T;
  }

  return (await response.blob()) as unknown as T;
}
